import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Code as CodeIcon,
  Style as StyleIcon,
  FormatPaint as FormatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  TextFields as TextFieldsIcon,
  Opacity as OpacityIcon,
  SpaceBar as SpaceBarIcon,
  Numbers as NumbersIcon,
  ViewAgenda as ViewAgendaIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  TableChart as TableChartIcon,
  FormatListBulleted as ListIcon,
  Highlight as HighlightIcon,
  Language as LanguageIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  Functions as FunctionsIcon,
  Input as InputIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const PDFCustomCode = ({
  customCSS,
  customJS,
  jsEnabled,
  onCSSChange,
  onJSChange,
  onJSEnabledChange
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [showCSSHelp, setShowCSSHelp] = useState(false);
  const [showJSHelp, setShowJSHelp] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('');
  
  // CSS snippets
  const cssSnippets = [
    {
      label: 'Set Default Font',
      code: `/* Import font from Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

/* Apply as default font for all content */
.pdf-page, .page, .content {
  font-family: 'Roboto', sans-serif !important;
}

/* This will be the default, but editor selections will override it */
.pdf-page *, .page *, .content * {
  font-family: inherit;
}

/* Alternative: Use system fonts */
/* .pdf-page, .page, .content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif !important;
} */`
    },
    {
      label: 'Solid Background',
      code: `/* Solid color background for all pages */
.pdf-page, .page {
  background-color: #f5f5f5 !important;
}

/* Alternative colors */
/* Light blue: background-color: #e3f2fd !important; */
/* Light yellow: background-color: #fffde7 !important; */
/* Light green: background-color: #f1f8e9 !important; */
/* Cream: background-color: #fef7e0 !important; */`
    },
    {
      label: 'Gradient Background',
      code: `/* Gradient background for all pages */
.pdf-page, .page {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

/* Alternative gradients */

/* Subtle gradient */
/* .pdf-page, .page {
  background: linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%) !important;
} */

/* Sunset gradient */
/* .pdf-page, .page {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%) !important;
} */

/* Ocean gradient */
/* .pdf-page, .page {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%) !important;
} */

/* Professional gradient */
/* .pdf-page, .page {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%) !important;
} */`
    },
    {
      label: 'Text Watermark',
      code: `/* CSS-based text watermark pattern */
.pdf-page::before, .page::before {
  content: "CONFIDENTIAL CONFIDENTIAL CONFIDENTIAL";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  font-size: 120px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.05);
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
}

/* Ensure content appears above watermark */
.pdf-page *, .page * {
  position: relative;
  z-index: 1;
}

/* Alternative: Repeating pattern watermark */
/* .pdf-page::after, .page::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 100px,
    rgba(0, 0, 0, 0.03) 100px,
    rgba(0, 0, 0, 0.03) 200px
  );
  pointer-events: none;
  z-index: 0;
} */

/* Alternative: Multiple text watermarks */
/* .pdf-page::before, .page::before {
  content: "DRAFT\\A DRAFT\\A DRAFT\\A DRAFT\\A DRAFT";
  white-space: pre;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 80px;
  font-weight: 700;
  color: rgba(255, 0, 0, 0.08);
  letter-spacing: 20px;
  pointer-events: none;
  z-index: 0;
} */`
    },
    {
      label: 'Complete Theme',
      code: `/* Complete styling example with font, background, and watermark */
@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&display=swap');

/* Default font for all content */
.pdf-page, .page {
  font-family: 'Merriweather', serif !important;
  /* Subtle gradient background */
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%) !important;
  position: relative;
}

/* CSS Watermark */
.pdf-page::after, .page::after {
  content: "CONFIDENTIAL";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 100px;
  font-weight: bold;
  color: rgba(200, 200, 200, 0.1);
  pointer-events: none;
  z-index: 0;
}

/* Ensure content is above watermark */
.pdf-page *, .page * {
  position: relative;
  z-index: 1;
}`
    },
    {
      label: 'Page Margins',
      code: `/* Adjust page margins and padding */
.pdf-page, .page {
  padding: 30mm 25mm !important; /* top/bottom: 30mm, left/right: 25mm */
}

/* Or set individual margins */
/* .pdf-page, .page {
  padding-top: 40mm !important;
  padding-bottom: 30mm !important;
  padding-left: 25mm !important;
  padding-right: 25mm !important;
} */`
    },
    {
      label: 'Page Numbers',
      code: `/* Add page numbers to all pages */
.pdf-page::after, .page::after {
  content: counter(page);
  position: absolute;
  bottom: 20mm;
  right: 25mm;
  font-size: 12px;
  color: #666;
  counter-increment: page;
}

/* Initialize counter */
body {
  counter-reset: page;
}

/* Alternative: Bottom center */
/* .pdf-page::after, .page::after {
  content: "Page " counter(page);
  position: absolute;
  bottom: 15mm;
  left: 50%;
  transform: translateX(-50%);
} */`
    },
    {
      label: 'Header/Footer',
      code: `/* Add header to all pages */
.pdf-page::before, .page::before {
  content: "Company Name | Document Title";
  position: absolute;
  top: 10mm;
  left: 20mm;
  right: 20mm;
  font-size: 10px;
  color: #999;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 5px;
}

/* Add footer */
.pdf-page::after, .page::after {
  content: "© 2024 Company Name - Confidential";
  position: absolute;
  bottom: 10mm;
  left: 20mm;
  right: 20mm;
  font-size: 10px;
  color: #999;
  text-align: center;
  border-top: 1px solid #e0e0e0;
  padding-top: 5px;
}`
    },
    {
      label: 'Hide in Print',
      code: `/* Hide elements when printing */
@media print {
  .no-print {
    display: none !important;
  }
  
  /* Hide buttons and interactive elements */
  button, .button-wrapper {
    display: none !important;
  }
  
  /* Show print-only content */
  .print-only {
    display: block !important;
  }
  
  /* Remove shadows and borders for cleaner print */
  .pdf-page, .page {
    box-shadow: none !important;
    border: none !important;
  }
}`
    },
    {
      label: 'First Page Special',
      code: `/* Special styling for first page (cover page) */
.pdf-page:first-child, .page:first-child {
  /* Different background */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  
  /* Center all content */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

/* Make first page headings white */
.pdf-page:first-child h1,
.pdf-page:first-child h2,
.pdf-page:first-child p {
  color: white !important;
}

/* Remove header/footer from first page */
.pdf-page:first-child::before,
.pdf-page:first-child::after {
  display: none !important;
}`
    },
    {
      label: 'Two Column Layout',
      code: `/* Two column layout for content */
.pdf-page .content, .page .content {
  column-count: 2;
  column-gap: 20px;
  column-rule: 1px solid #e0e0e0;
}

/* Prevent column breaks in headers */
h1, h2, h3, h4, h5, h6 {
  break-inside: avoid;
  break-after: avoid;
}

/* Keep images in single column */
img {
  max-width: 100%;
  break-inside: avoid;
}

/* Full width elements */
.full-width {
  column-span: all;
}`
    },
    {
      label: 'Invoice/Report Style',
      code: `/* Professional invoice/report styling */
.pdf-page, .page {
  font-family: 'Arial', sans-serif !important;
  color: #333 !important;
  line-height: 1.6;
}

/* Header section */
.header-section {
  border-bottom: 3px solid #2196F3;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

/* Invoice table */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

table th {
  background: #2196F3 !important;
  color: white !important;
  padding: 12px !important;
  text-align: left !important;
  font-weight: bold !important;
}

table td {
  padding: 10px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

table tr:nth-child(even) {
  background: #f9f9f9 !important;
}

/* Total section */
.total-section {
  text-align: right;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #333;
  font-size: 1.2em;
  font-weight: bold;
}`
    },
    {
      label: 'Letter/Contract Style',
      code: `/* Formal letter/contract styling */
@import url('https://fonts.googleapis.com/css2?family=Times+New+Roman&display=swap');

.pdf-page, .page {
  font-family: 'Times New Roman', serif !important;
  font-size: 12pt;
  line-height: 1.8;
  text-align: justify;
}

/* Letterhead space */
.pdf-page:first-child, .page:first-child {
  padding-top: 60mm !important;
}

/* Signature block */
.signature-block {
  margin-top: 50px;
  display: flex;
  justify-content: space-between;
}

.signature-line {
  border-bottom: 1px solid #000;
  width: 200px;
  margin-top: 60px;
}

/* Date and reference */
.date-block {
  text-align: right;
  margin-bottom: 30px;
}

/* Indented paragraphs */
p {
  text-indent: 0.5in;
  margin-bottom: 12pt;
}`
    },
    {
      label: 'Academic Paper',
      code: `/* Academic paper styling (APA-like) */
@import url('https://fonts.googleapis.com/css2?family=Times+New+Roman&display=swap');

.pdf-page, .page {
  font-family: 'Times New Roman', serif !important;
  font-size: 12pt;
  line-height: 2;
  text-align: left;
  padding: 1in !important;
}

/* Title page */
.pdf-page:first-child h1 {
  text-align: center;
  margin-top: 25%;
  font-size: 16pt;
  font-weight: bold;
}

/* Headers */
h1 { font-size: 14pt; text-align: center; font-weight: bold; }
h2 { font-size: 12pt; text-align: left; font-weight: bold; }
h3 { font-size: 12pt; text-align: left; font-style: italic; }

/* References */
.references {
  hanging-indent: 0.5in;
}

.references p {
  text-indent: -0.5in;
  padding-left: 0.5in;
}

/* Block quotes */
blockquote {
  margin-left: 0.5in;
  margin-right: 0.5in;
  font-size: 11pt;
}`
    },
    {
      label: 'Header Styling',
      code: `h1 {
  color: #2196F3;
  border-bottom: 2px solid #2196F3;
  padding-bottom: 10px;
}`
    },
    {
      label: 'Table Styling',
      code: `table {
  width: 100%;
  border-collapse: collapse;
}

table th {
  background-color: #f5f5f5;
  font-weight: bold;
  text-align: left;
  padding: 12px;
}

table td {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
}`
    },
    {
      label: 'Print-only Content',
      code: `@media print {
  .no-print {
    display: none !important;
  }
  
  .print-only {
    display: block !important;
  }
}`
    },
    {
      label: 'Custom Button Styles',
      code: `/* IMPORTANT: Use !important to override inline styles */

/* Example: Red button with dark border */
.testButton {
  background-color: red !important;
  color: white !important;
  border: 2px solid darkred !important;
}

/* Success button (green) */
.btn-success {
  background-color: #4CAF50 !important;
  color: white !important;
  border: none !important;
}

/* Warning button (orange) */
.btn-warning {
  background-color: #ff9800 !important;
  color: white !important;
  border: none !important;
}

/* Gradient button with shadow */
.btn-gradient {
  background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%) !important;
  color: white !important;
  box-shadow: 0 3px 5px 2px rgba(255, 105, 135, .3) !important;
  border: none !important;
}

/* Large rounded button */
.btn-large-rounded {
  padding: 16px 40px !important;
  font-size: 20px !important;
  border-radius: 50px !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
}`
    },
    {
      label: 'Custom Input Styles',
      code: `/* IMPORTANT: Use !important to override inline styles */

/* Modern input style */
.input-modern {
  border: none !important;
  border-bottom: 2px solid #0F3A61 !important;
  border-radius: 0 !important;
  background-color: transparent !important;
  padding: 8px 0 !important;
}

/* Rounded input */
.input-rounded {
  border-radius: 25px !important;
  padding: 10px 20px !important;
}

/* Material Design style */
.input-material {
  border: none !important;
  border-bottom: 1px solid #ccc !important;
  border-radius: 0 !important;
  padding: 8px 0 !important;
  background: transparent !important;
}

/* Error state */
.input-error {
  border-color: #f44336 !important;
  background-color: #ffebee !important;
}

/* Success state */
.input-success {
  border-color: #4caf50 !important;
  background-color: #e8f5e9 !important;
}

/* Disabled style */
.input-disabled {
  background-color: #f5f5f5 !important;
  color: #9e9e9e !important;
  cursor: not-allowed !important;
}

/* Input with icon space */
.input-with-icon {
  padding-left: 40px !important;
}

/* Large input */
.input-large {
  padding: 16px 24px !important;
  font-size: 20px !important;
}

/* Transparent input */
.input-transparent {
  background-color: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: white !important;
}

/* Custom label styles */
.ql-custom-input .input-label {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 12px !important;
  color: #0F3A61 !important;
}`
    },
    {
      label: 'Enhanced Blockquotes',
      code: `blockquote {
  position: relative;
  padding: 30px 40px 20px 60px;
  margin: 2em 0;
  background: rgba(242, 197, 38, 0.05);
  border-left: 5px solid #f2c526;
  font-style: italic;
  color: #4A6785;
}

blockquote:before {
  content: "\\201C";
  position: absolute;
  left: 15px;
  top: 15px;
  font-size: 60px;
  line-height: 1;
  color: #f2c526;
  opacity: 0.5;
}`
    },
    {
      label: 'Alert Boxes',
      code: `.alert {
  padding: 15px 20px;
  margin: 1em 0;
  border-radius: 8px;
  border-left: 4px solid;
  font-family: 'Poppins', sans-serif;
}

.alert-info {
  background: rgba(15, 58, 97, 0.06);
  border-color: #0F3A61;
  color: #0F3A61;
}

.alert-success {
  background: rgba(76, 175, 80, 0.1);
  border-color: #4caf50;
  color: #2e7d32;
}

.alert-warning {
  background: rgba(242, 197, 38, 0.1);
  border-color: #f2c526;
  color: #f39c12;
}

.alert-error {
  background: rgba(244, 67, 54, 0.1);
  border-color: #f44336;
  color: #c62828;
}`
    },
    {
      label: 'Arabic/RTL Support',
      code: `[dir="rtl"] {
  font-family: 'Cairo', 'Tajawal', sans-serif;
  text-align: right;
}

.arabic {
  font-family: 'Cairo', 'Tajawal', sans-serif;
  font-size: 1.1em;
  line-height: 2;
  direction: rtl;
}

.mixed-content {
  unicode-bidi: plaintext;
}`
    },
    {
      label: 'Highlighted Text',
      code: `.highlight {
  background: linear-gradient(180deg, transparent 50%, #f2c526 50%);
  padding: 2px 0;
}

.important {
  background: #0F3A61;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: bold;
}

mark {
  background: rgba(242, 197, 38, 0.3);
  padding: 2px 4px;
  border-radius: 2px;
  color: #0F3A61;
}`
    },
    {
      label: 'Custom Lists',
      code: `ul.custom-list {
  list-style: none;
  padding-left: 0;
}

ul.custom-list li {
  position: relative;
  padding-left: 30px;
  margin-bottom: 10px;
}

ul.custom-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #4caf50;
  font-weight: bold;
  font-size: 1.2em;
}`
    },
    {
      label: 'Sidebar/Callout',
      code: `.sidebar {
  float: right;
  width: 300px;
  margin: 0 0 20px 20px;
  padding: 20px;
  background: rgba(15, 58, 97, 0.04);
  border: 1px solid rgba(15, 58, 97, 0.15);
  border-radius: 8px;
  color: #0F3A61;
}

.callout {
  background: rgba(242, 197, 38, 0.08);
  border-left: 4px solid #f2c526;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;
  color: #0F3A61;
}`
    },
    {
      label: 'Page Header/Footer',
      code: `@page {
  @top-center {
    content: "Document Title";
    font-family: 'Poppins', sans-serif;
    font-size: 10pt;
    color: #0F3A61;
  }
  
  @bottom-center {
    content: counter(page) " of " counter(pages);
    font-family: 'Poppins', sans-serif;
    font-size: 10pt;
    color: #4A6785;
  }
}

.page-header {
  position: running(header);
  font-family: 'Poppins', sans-serif;
  color: #0F3A61;
  border-bottom: 1px solid #f2c526;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.page-footer {
  position: running(footer);
  font-family: 'Poppins', sans-serif;
  color: #4A6785;
  border-top: 1px solid #f2c526;
  padding-top: 10px;
  margin-top: 20px;
  text-align: center;
}`
    }
  ];
  
  // Organize CSS snippets into categories
  const cssCategories = [
    {
      name: 'Page Layout',
      icon: <ViewAgendaIcon />,
      color: '#4CAF50',
      snippets: ['Solid Background', 'Gradient Background', 'Page Margins', 'Two Column Layout', 'First Page Special']
    },
    {
      name: 'Typography & Fonts',
      icon: <TextFieldsIcon />,
      color: '#2196F3',
      snippets: ['Set Default Font', 'Header Styling']
    },
    {
      name: 'Watermarks & Overlays',
      icon: <OpacityIcon />,
      color: '#9C27B0',
      snippets: ['Text Watermark', 'Complete Theme']
    },
    {
      name: 'Headers & Footers',
      icon: <ViewAgendaIcon />,
      color: '#FF9800',
      snippets: ['Page Numbers', 'Header/Footer']
    },
    {
      name: 'Document Templates',
      icon: <DescriptionIcon />,
      color: '#00BCD4',
      snippets: ['Invoice/Report Style', 'Letter/Contract Style', 'Academic Paper']
    },
    {
      name: 'Tables & Lists',
      icon: <TableChartIcon />,
      color: '#795548',
      snippets: ['Table Styling', 'Custom Lists']
    },
    {
      name: 'Special Effects',
      icon: <HighlightIcon />,
      color: '#E91E63',
      snippets: ['Alert Boxes', 'Highlighted Text', 'Sidebar/Callout']
    },
    {
      name: 'Print & Export',
      icon: <VisibilityIcon />,
      color: '#607D8B',
      snippets: ['Hide in Print', 'Print-only Content']
    },
    {
      name: 'RTL & Languages',
      icon: <LanguageIcon />,
      color: '#3F51B5',
      snippets: ['Arabic/RTL Support']
    }
  ];
  
  // JS snippets
  const jsSnippets = [
    {
      label: 'Dynamic Date',
      code: `document.querySelectorAll('.current-date').forEach(el => {
  el.textContent = new Date().toLocaleDateString();
});`
    },
    {
      label: 'Auto-numbering',
      code: `let counter = 1;
document.querySelectorAll('.auto-number').forEach(el => {
  el.textContent = counter++;
});`
    },
    {
      label: 'Calculate Total',
      code: `const total = Array.from(document.querySelectorAll('.price'))
  .reduce((sum, el) => sum + parseFloat(el.textContent || 0), 0);
  
document.querySelector('.total').textContent = total.toFixed(2);`
    }
  ];
  
  // Handle snippet insert
  const insertSnippet = (code, isCSS = true) => {
    if (isCSS) {
      onCSSChange((customCSS || '') + '\n\n' + code);
    } else {
      onJSChange((customJS || '') + '\n\n' + code);
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // TODO: Show notification
  };
  
  // Format CSS
  const formatCSS = () => {
    try {
      // Basic CSS formatting
      const formatted = customCSS
        .replace(/\s*{\s*/g, ' {\n  ')
        .replace(/;\s*/g, ';\n  ')
        .replace(/\s*}\s*/g, '\n}\n')
        .replace(/\n\s*\n/g, '\n');
      onCSSChange(formatted);
    } catch (err) {
      console.error('CSS formatting error:', err);
    }
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ borderRadius: 0 }} elevation={1}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<StyleIcon />} label="Custom CSS" />
          <Tab 
            icon={<CodeIcon />} 
            label="Custom JavaScript" 
            disabled={!jsEnabled}
          />
        </Tabs>
      </Paper>
      
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Code Editor */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {activeTab === 0 ? (
            <>
              {/* CSS Editor */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Custom Styles</Typography>
                  <Box>
                    <Tooltip title="Format CSS">
                      <IconButton size="small" onClick={formatCSS}>
                        <FormatIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy CSS">
                      <IconButton size="small" onClick={() => copyToClipboard(customCSS || '')}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="CSS Help">
                      <IconButton size="small" onClick={() => setShowCSSHelp(!showCSSHelp)}>
                        {showCSSHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              
              <Collapse in={showCSSHelp}>
                <Alert severity="info" sx={{ m: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>CSS Tips:</Typography>
                  <Typography variant="caption" component="div">
                    • Use @page rules for print-specific styling<br />
                    • Add .page-break class for manual page breaks<br />
                    • Use @media print for print-only styles<br />
                    • Avoid absolute positioning for better PDF rendering<br />
                    <br />
                    <strong>Button & Input Styling:</strong><br />
                    • Use <code>!important</code> to override inline styles<br />
                    • Target buttons with: <code>.ql-custom-button a.yourClass</code><br />
                    • Target inputs with: <code>.ql-custom-input input.yourClass</code><br />
                    • Example: <code>{`.testButton { background-color: red !important; }`}</code><br />
                    <br />
                    <strong>Note:</strong> Input fields are only available in Interactive PDF mode
                  </Typography>
                </Alert>
              </Collapse>
              
              <TextField
                multiline
                fullWidth
                value={customCSS || ''}
                onChange={(e) => onCSSChange(e.target.value)}
                placeholder="/* Add your custom CSS here */&#10;&#10;body {&#10;  font-family: Arial, sans-serif;&#10;}"
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    height: '100%'
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
                InputProps={{
                  sx: { height: '100%' }
                }}
              />
            </>
          ) : (
            <>
              {/* JavaScript Editor */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2">Custom JavaScript</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={jsEnabled}
                          onChange={(e) => onJSEnabledChange(e.target.checked)}
                        />
                      }
                      label="Enable"
                    />
                  </Box>
                  <Box>
                    <Tooltip title="Copy JavaScript">
                      <IconButton size="small" onClick={() => copyToClipboard(customJS || '')}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="JavaScript Help">
                      <IconButton size="small" onClick={() => setShowJSHelp(!showJSHelp)}>
                        {showJSHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              
              <Collapse in={showJSHelp}>
                <Alert severity="warning" sx={{ m: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>JavaScript Security:</Typography>
                  <Typography variant="caption" component="div">
                    • JavaScript runs in a sandboxed environment<br />
                    • No access to external APIs or user data<br />
                    • DOM manipulation only within the PDF content<br />
                    • Scripts execute once when PDF is generated
                  </Typography>
                </Alert>
              </Collapse>
              
              {!jsEnabled ? (
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 4
                }}>
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="subtitle1" gutterBottom>
                      JavaScript is disabled
                    </Typography>
                    <Typography variant="body2">
                      Enable JavaScript in settings to add custom scripts to your PDF
                    </Typography>
                  </Alert>
                </Box>
              ) : (
                <TextField
                  multiline
                  fullWidth
                  value={customJS || ''}
                  onChange={(e) => onJSChange(e.target.value)}
                  placeholder="// Add your custom JavaScript here&#10;&#10;// Example: Update all elements with class 'date'&#10;document.querySelectorAll('.date').forEach(el => {&#10;  el.textContent = new Date().toLocaleDateString();&#10;});"
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      height: '100%'
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important'
                    }
                  }}
                  InputProps={{
                    sx: { height: '100%' }
                  }}
                />
              )}
            </>
          )}
        </Box>
        
        {/* Snippets Sidebar */}
        <Paper sx={{ 
          width: 300, 
          borderRadius: 0,
          borderLeft: 1,
          borderColor: 'divider',
          overflow: 'auto'
        }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              {activeTab === 0 ? 'CSS Snippets' : 'JavaScript Snippets'}
            </Typography>
          </Box>
          
          {activeTab === 0 ? (
            // CSS Snippets - Categorized view
            <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
              {cssCategories.map((category, catIndex) => (
                <Box key={catIndex}>
                  <Box
                    onClick={() => setExpandedCategory(expandedCategory === category.name ? '' : category.name)}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: expandedCategory === category.name ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box sx={{ color: category.color }}>{category.icon}</Box>
                    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
                      {category.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {category.snippets.length}
                    </Typography>
                    {expandedCategory === category.name ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={expandedCategory === category.name}>
                    <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category.snippets.map(snippetLabel => {
                          const snippet = cssSnippets.find(s => s.label === snippetLabel);
                          if (!snippet) return null;
                          return (
                            <Chip
                              key={snippetLabel}
                              label={snippetLabel}
                              onClick={() => insertSnippet(snippet.code, true)}
                              size="small"
                              sx={{
                                bgcolor: alpha(category.color, 0.1),
                                borderColor: category.color,
                                color: category.color,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: alpha(category.color, 0.2),
                                  borderColor: category.color
                                }
                              }}
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          ) : (
            // JS Snippets - Simple list view
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {jsSnippets.map((snippet, index) => (
                  <Tooltip 
                    key={index} 
                    title="Click to insert this snippet"
                    placement="top"
                  >
                    <Chip
                      label={snippet.label}
                      onClick={() => insertSnippet(snippet.code, false)}
                      variant="outlined"
                      size="small"
                      icon={<CodeIcon fontSize="small" />}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '12px',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main',
                          color: 'primary.main'
                        }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PDFCustomCode;